import { useCallback, useState } from "react";
import { Loader2, PhoneCall } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover } from "@/components/ui/popover";
import config from "@/config";
import { cn } from "@/utils";
import type { VapiConcurrencySnapshot } from "@/types";

async function readErrorMessage(res: Response): Promise<string> {
  const raw = await res.text();
  try {
    const body = JSON.parse(raw) as { detail?: unknown };
    if (typeof body.detail === "string") return body.detail;
  } catch {
    /* not JSON */
  }
  return raw || `HTTP ${res.status}`;
}

function formatAsOf(iso?: string | null): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

export function VapiConcurrencyButton() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<VapiConcurrencySnapshot | null>(null);

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.API_BASE_URL}/api/dashboard/vapi-concurrency`);
      if (!res.ok) throw new Error(await readErrorMessage(res));
      const data = (await res.json()) as VapiConcurrencySnapshot;
      setSnapshot(data);
    } catch (e) {
      setSnapshot(null);
      setError(e instanceof Error ? e.message : "Failed to load VAPI concurrency");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = useCallback(() => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    void fetchSnapshot();
  }, [open, fetchSnapshot]);

  const current = snapshot?.current;
  const limit = snapshot?.limit ?? null;
  const remaining = snapshot?.remaining ?? null;
  const nearCap =
    typeof remaining === "number" &&
    typeof limit === "number" &&
    remaining <= Math.max(1, Math.ceil(limit * 0.1));
  const atCap = typeof remaining === "number" && remaining <= 0 && typeof limit === "number";

  let label = "VAPI concurrency";
  if (typeof current === "number" && !error) {
    label = typeof limit === "number" ? `${current} / ${limit}` : `${current} in use`;
  }

  return (
    <Popover
      isOpen={open}
      onToggle={handleToggle}
      side="bottom"
      align="end"
      className="w-72 p-3"
      trigger={
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading}
          className={cn(
            "gap-1",
            atCap && "border-red-400 text-red-700",
            !atCap && nearCap && "border-amber-400 text-amber-800",
          )}
          aria-label="Check VAPI call concurrency"
          aria-busy={loading}
        >
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <PhoneCall className="h-3.5 w-3.5" />
          )}
          {label}
        </Button>
      }
    >
      <div className="space-y-2 text-sm">
        <p className="font-medium leading-none">VAPI call slots</p>
        {loading ? (
          <p className="text-muted-foreground">Checking live concurrency…</p>
        ) : error ? (
          <p className="text-destructive" role="alert">
            {error}
          </p>
        ) : snapshot ? (
          <>
            <p>
              <span className="text-2xl font-semibold tabular-nums">{snapshot.current}</span>
              {typeof limit === "number" ? (
                <span className="text-muted-foreground"> / {limit} in use</span>
              ) : (
                <span className="text-muted-foreground"> in use</span>
              )}
            </p>
            {typeof snapshot.peak_15m === "number" ? (
              <p className="text-muted-foreground">
                Peak last 15 min:{" "}
                <span className="font-medium text-foreground tabular-nums">{snapshot.peak_15m}</span>
              </p>
            ) : null}
            {typeof remaining === "number" ? (
              <p className={cn("text-muted-foreground", atCap && "text-red-700", nearCap && !atCap && "text-amber-800")}>
                {remaining} remaining
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                VAPI does not expose the plan cap on this token. Set{" "}
                <code className="rounded bg-muted px-1">VAPI_CONCURRENCY_LIMIT</code> on the backend
                to show remaining slots.
              </p>
            )}
            {snapshot.as_of ? (
              <p className="text-xs text-muted-foreground">As of {formatAsOf(snapshot.as_of)}</p>
            ) : null}
          </>
        ) : (
          <p className="text-muted-foreground">Click to load current concurrency.</p>
        )}
      </div>
    </Popover>
  );
}
